/**
 * Terms Acceptance Gate
 * Blocks app access until user has accepted current Terms of Service and Privacy Policy.
 * Sits between ProtectedRoute and page content.
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Label, Spinner, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui';
import { TERMS_CONFIG } from '@aerotage/shared';
import { userApi } from '../services/api';

const WEBSITE_URL = import.meta.env.VITE_WEBSITE_URL as string;
const TERMS_URL = `${WEBSITE_URL}${TERMS_CONFIG.TERMS_PATH}`;
const PRIVACY_URL = `${WEBSITE_URL}${TERMS_CONFIG.PRIVACY_PATH}`;

interface TermsAcceptanceGateProps {
  children: React.ReactNode;
}

export function TermsAcceptanceGate({ children }: TermsAcceptanceGateProps) {
  const [status, setStatus] = useState<'loading' | 'accepted' | 'pending'>('loading');
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkTermsStatus = useCallback(async () => {
    try {
      const result = await userApi.getTermsStatus();
      setStatus(result.accepted ? 'accepted' : 'pending');
    } catch (_err) {
      setStatus('pending');
    }
  }, []);

  useEffect(() => {
    checkTermsStatus();
  }, [checkTermsStatus]);

  const handleAccept = async () => {
    setError(null);
    setSubmitting(true);

    try {
      await userApi.acceptTerms(
        TERMS_CONFIG.CURRENT_TERMS_VERSION,
        TERMS_CONFIG.CURRENT_PRIVACY_VERSION,
      );
      setStatus('accepted');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to record acceptance. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted">
        <Spinner size="lg" />
      </div>
    );
  }

  if (status === 'accepted') {
    return <>{children}</>;
  }

  return (
    <Dialog open={true}>
      <DialogContent
        className="max-w-lg [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <img src="/aerotage-logo.svg" alt="Aerotage" className="h-10 mx-auto mb-2" />
          <DialogTitle>Terms of Service Update</DialogTitle>
          <DialogDescription>
            Please review and accept our Terms of Service and Privacy Policy to continue
            using the platform.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 shrink-0 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-link hover:underline"
            >
              Terms of Service
            </a>
          </div>
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 shrink-0 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
            </svg>
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-link hover:underline"
            >
              Privacy Policy
            </a>
          </div>
        </div>

        {error && (
          <div className="bg-destructive-light border border-destructive text-destructive p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <Label className="flex items-start gap-3 cursor-pointer">
          {/* eslint-disable-next-line no-restricted-syntax -- checkbox inputs require native element */}
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-foreground">
            I have read and accept the Terms of Service and Privacy Policy.
          </span>
        </Label>

        <Button
          onClick={handleAccept}
          disabled={!checked || submitting}
          className="w-full h-11 font-semibold"
        >
          {submitting ? 'Accepting...' : 'Accept & Continue'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
